import { faroCoreApiClient } from "@api/faro-core-api-client";
import InfoIcon from "@assets/icons/new/info-circle-fill.svg?react";
import {
  SPACE_ELEMENTS_OF_MODAL,
  STACK_ERROR_SX,
} from "@components/common/dialog/faro-dialog";
import { GroupAutoComplete } from "@components/common/group-autocomplete/group-autocomplete";
import { LabelWithHelp } from "@components/common/label-with-help";
import {
  MemberAutocomplete,
  SelectedMember,
} from "@components/common/member-autocomplete/member-autocomplete";
import { useGroupMembersContext } from "@context-providers/group-members-provider";
import { blue, Dialog, neutral } from "@flat-ui";
import { Alert, Stack } from "@mui/material";
import { useAppParams } from "@router/router-helper";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { updateProjectDetails } from "@store/projects/projects-slice-thunk";
import { useAppDispatch } from "@store/store-helper";
import { isObject } from "lodash";
import { PropsWithChildren, useEffect, useState } from "react";

/** A dialog to change the project manager and group of the current project */
export function ChangeManagementDialog({
  children,
}: PropsWithChildren): JSX.Element {
  const dispatch = useAppDispatch();
  const { companyId } = useAppParams();

  const {
    project,
    groupMembers,
    setSelectedGroupId,
    selectedGroupId,
    canChangeManagement,
  } = useGroupMembersContext();

  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [selectedProjectManager, setSelectedProjectManager] =
    useState<SelectedMember>(null);

  // Prefill the project manager with the current project manager, if the current group of the project is selected
  // Empty it for other groups
  useEffect(() => {
    if (project && isDialogOpen && project.group) {
      if (project.group.id !== selectedGroupId) {
        setSelectedProjectManager(null);
        return;
      }

      const projectManager =
        groupMembers.find(
          (member) =>
            member.identity === project.managers?.projectManager?.identity
        ) ?? null;
      setSelectedProjectManager(projectManager);
    }
  }, [isDialogOpen, groupMembers, project, selectedGroupId]);

  /**
   * Sends a request to the backend to change the project manager and group of the project
   */
  async function changeProjectManagerAndGroup(): Promise<void> {
    if (!companyId) {
      throw new Error("Company id is required!");
    }

    // Theoretically the user should not be able to reach this state
    // if the project manager was not provided but do this for type safety
    // and as an edge case if our members selection didn't work properly.
    if (!selectedProjectManager) {
      throw new Error("Project manager is required!");
    }

    if (!isObject(selectedProjectManager)) {
      const payload: FaroCoreApiTypes.BulkMemberInvitePayload<"company"> = {
        assignments: [
          {
            identities: [selectedProjectManager],
            role: "PROJECT_MANAGER",
          },
        ],
      };

      await faroCoreApiClient.company.addMembersToCompany({
        companyId,
        payload,
      });
    }

    const payload: FaroCoreApiTypes.UpdateProjectDetailsPayloadProps = {
      projectManagerIdentity: isObject(selectedProjectManager)
        ? selectedProjectManager.identity
        : selectedProjectManager,
      groupId: String(selectedGroupId),
    };

    void dispatch(updateProjectDetails({ payload }));

    setIsDialogOpen(false);
  }

  return (
    <>
      <div
        style={canChangeManagement ? { cursor: "pointer" } : undefined}
        onClick={() => setIsDialogOpen(canChangeManagement)}
      >
        {children}
      </div>
      <Dialog
        title="Change Management"
        confirmText="Change"
        open={isDialogOpen}
        onConfirm={changeProjectManagerAndGroup}
        isConfirmDisabled={!selectedProjectManager}
        onCancel={() => setIsDialogOpen(false)}
      >
        <Alert
          icon={<InfoIcon color={blue[500]} />}
          severity="info"
          sx={{
            marginBottom: SPACE_ELEMENTS_OF_MODAL,
            backgroundColor: blue[100],
            color: neutral[1000],
          }}
        >
          Be aware that changing a group can ultimately impact the project
          manager.
        </Alert>
        <Stack sx={STACK_ERROR_SX}>
          <LabelWithHelp title="Group" />
          <GroupAutoComplete
            callbackOnGroupSelected={(groupId) => setSelectedGroupId(groupId)}
            selectedGroupId={selectedGroupId}
            shouldOnlyShowGroupsWithGroupManager={true}
          />
        </Stack>
        <Stack>
          <LabelWithHelp title="Manager" isDisabled={!selectedGroupId} />
          <MemberAutocomplete
            members={groupMembers}
            selectedMember={selectedProjectManager}
            onOptionChange={setSelectedProjectManager}
            isDisabled={!selectedGroupId}
            placeholder="Choose who can manage this project..."
          />
        </Stack>
      </Dialog>
    </>
  );
}
